import React, { ReactElement, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { WW } from '_utils';
import { track } from '_utils/Amplitude';
import { screenMargin, smallestFontSize, smallestMargin } from '_utils/sizes';
import { CooperativeGroupDropdown, HeimeText, PrimaryButton } from 'Components';
import Accordion from 'Components/Accordion';
import FormRichText from 'Components/Form/FormRichText/FormRichText';
import SelectorChip from 'Components/SelectorChip';
import Tip from 'Components/Tip';
import { HelpfulNeighborTag, HelpfulNeighborTags } from 'types/message';
import { FormValues } from '../SharingNew';

const HelpNeighbor = ({
    onSubmit,
    isLoading,
}: {
    onSubmit: (values: FormValues) => void;
    isLoading?: boolean;
}): ReactElement => {
    const { t } = useTranslation();
    const { handleSubmit, formState, watch, setValue } = useFormContext<FormValues>();
    const [tipsOpen, setTipsOpen] = useState(false);

    const handleTipsOpen = (open: boolean) => {
        if (open) {
            track('Become helpful neighbor tips opened');
        }
        setTipsOpen(open);
    };

    const isEditing = watch('isEditing');
    const disableButton = !formState.isValid;

    const selectedTags = watch('helpful_neighbor_tags');
    const handleToggleTag = (tag: HelpfulNeighborTag) => {
        const newVal = selectedTags.includes(tag) ? selectedTags.filter((c) => c !== tag) : [...selectedTags, tag];
        setValue('helpful_neighbor_tags', newVal, { shouldValidate: true });
    };

    return (
        <>
            <HeimeText variant="title" style={{ marginTop: screenMargin, marginBottom: smallestMargin }}>
                {t('sharingNew:helpNeighbor:title')}
            </HeimeText>
            <HeimeText variant="subtitle" style={{ marginBottom: screenMargin, textAlign: 'center' }}>
                {t('sharingNew:helpNeighbor:subtitle')}
            </HeimeText>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: smallestMargin }}>
                {HelpfulNeighborTags.map((category) => (
                    <SelectorChip
                        key={category}
                        onSelect={() => handleToggleTag(category)}
                        variant={selectedTags.includes(category) ? 'PrimaryWhite' : 'WhitePrimaryGrey'}
                        title={t(`sharingAll:${category}_title`)}
                        containerStyle={{
                            paddingVertical: smallestMargin,
                        }}
                        titleStyle={{
                            fontSize: smallestFontSize,
                        }}
                    />
                ))}
            </View>
            <FormRichText
                name="richtext_content"
                label={t('sharingNew:helpNeighbor:textDesc')}
                placeholder={t('sharingNew:helpNeighbor:enterDesc')}
            />
            <Accordion
                open={tipsOpen}
                onChange={handleTipsOpen}
                title={t('sharingNew:helpNeighbor:tip_text')}
                titleStyle={{ fontSize: smallestFontSize }}
                containerStyle={{
                    marginTop: smallestMargin,
                }}
            >
                <Tip
                    title={t('sharingNew:helpNeighbor:tip_1:title')}
                    description={t('sharingNew:helpNeighbor:tip_1:desc')}
                />
                <Tip
                    title={t('sharingNew:helpNeighbor:tip_2:title')}
                    description={t('sharingNew:helpNeighbor:tip_2:desc')}
                />
                <Tip
                    title={t('sharingNew:helpNeighbor:tip_3:title')}
                    description={t('sharingNew:helpNeighbor:tip_3:desc')}
                />
                <Tip
                    title={t('sharingNew:helpNeighbor:tip_4:title')}
                    description={t('sharingNew:helpNeighbor:tip_4:desc')}
                />
            </Accordion>
            <CooperativeGroupDropdown isCreatingNew={!isEditing} />
            <PrimaryButton
                style={{
                    marginTop: WW * 0.04,
                    marginBottom: WW * 0.04,
                }}
                status={isLoading ? 'loading' : disableButton ? 'disabled' : null}
                text={t('sharingNew:publish').toUpperCase()}
                onPress={handleSubmit(onSubmit)}
            />
        </>
    );
};

export default HelpNeighbor;
